/**
 * @module normalization schemas
 *
 * Functions that "normalize" data, or transform it into a known "entities".
 * They use "schemas" which simply assciate entity-types such that deeply nested
 * structures can be flattened.
 *
 * @see https://github.com/paularmstrong/normalizr
 */
import { schema } from 'normalizr';

const category = new schema.Entity('categories');

// A Shared Link survey's attributes have ids that can equal a company attribute's id, so they get their own key.
const employeeAttribute = new schema.Entity(
  'employeeAttributes',
  {},
  { idAttribute: (value) => (value.isOpenSurveyAttribute ? `open-survey-${value.id}` : value.id) },
);

const employee = new schema.Entity('employees', {
  employeeAttributes: [employeeAttribute],
});

const icon = new schema.Entity('icons');

const kioskAttribute = new schema.Entity('kioskAttributes');

const language = new schema.Entity('languages');

const timezone = new schema.Entity('timezones');

const user = new schema.Entity('users');

const role = new schema.Entity('roles');

const questionLogic = new schema.Entity('questionLogics');

const sendingEmailAddress = new schema.Entity('sendingEmailAddresses');

const surveyElement = new schema.Entity('surveyElements');

const survey = new schema.Entity('surveys', {
  surveyElements: [surveyElement],
  questionLogics: [questionLogic],
  categories: [category], // REFACTOR: surveys don't have categories (directly)
});

const trendRatingMeasurement = new schema.Entity('trendRatingMeasurements', {
  surveys: [survey],
});

const optionResponse = new schema.Entity('optionResponses');

const attributeValueResponse = new schema.Entity('attributeValueResponses', {
  optionResponses: [optionResponse],
});

const attributeResponse = new schema.Entity('attributeResponses', {
  employeeAttribute,
  attributeValueResponses: [attributeValueResponse],
});

const questionResponse = new schema.Entity('questionResponses', {
  surveyElement: surveyElement,
  optionResponses: [optionResponse],
  attributeResponses: [attributeResponse],
});

const surveyType = new schema.Entity('surveyTypes', {
  categories: [category],
});

const task = new schema.Entity('tasks', {
  surveys: [survey],
  categories: [category],
  users: [user],
});

const taskTemplate = new schema.Entity('taskTemplates', {
  categories: [category],
});

const learningTag = new schema.Entity('learningTags');

const learningContent = new schema.Entity('learningContents', {
  learningTags: [learningTag],
  categories: [category],
});

// REFACTOR: Prefer using "category" as label within the code, for consistency.
const factor = new schema.Entity('factors', {
  libraryActions: [taskTemplate],
  learningContent: [learningContent],
});

const overview = new schema.Entity('overview', {
  factors: [factor],
});

const company = new schema.Entity('companies', {
  timezones: [timezone],
  taskTemplates: [taskTemplate],
  kioskAttributes: [kioskAttribute],
  employeeAttributes: [employeeAttribute],
  sendingEmailAddress: [sendingEmailAddress],
});

const attributeValueResult = new schema.Entity('attributeValueResults');

const attributeResult = new schema.Entity('attributeResults', {
  employeeAttribute: employeeAttribute,
  attributeValueResults: [attributeValueResult],
});

const overallResult = new schema.Entity('overallResults', {
  attributeResults: [attributeResult],
});

const surveyElementResult = new schema.Entity('surveyElementResults', {
  surveyElement: surveyElement,
  attributeResults: [attributeResult],
});

const categoryResult = new schema.Entity('categoryResults', {
  category: category,
  surveyElementResults: [surveyElementResult],
  attributeResults: [attributeResult],
  taskTemplates: [taskTemplate],
  learningContents: [learningContent],
});

const compareResult = new schema.Entity('compareResults');

const surveyReminder = new schema.Entity('surveyReminders');

const benchmarkAttribute = new schema.Entity('benchmarkAttributes', {}, { idAttribute: 'name' });
const companyBenchmarkSetting = new schema.Entity('companyBenchmarkSettings', {}, { idAttribute: 'companyId' });
const benchmarkQuestion = new schema.Entity('benchmarkQuestions', {}, { idAttribute: 'questionId' });

const surveyTemplate = new schema.Entity('surveyTemplates');

const lifecycleSurveyType = new schema.Entity('lifecycleSurveyTypes');
const lifecycleSurveyTrigger = new schema.Entity('lifecycleSurveyTriggers', {
  survey: survey,
  lifecycleSurveyType: lifecycleSurveyType,
  triggerAttribute: employeeAttribute,
  adminApprovers: [user],
});
const lifecycleSurveyDoNotContactList = new schema.Entity('lifecycleSurveyDoNotContactList');

const rehireAttribute = new schema.Entity('rehireAttribute');

export {
  factor,
  overview,
  category,
  company,
  employee,
  employeeAttribute,
  icon,
  kioskAttribute,
  language,
  learningContent,
  learningTag,
  role,
  sendingEmailAddress,
  survey,
  surveyElement,
  surveyType,
  task,
  taskTemplate,
  timezone,
  trendRatingMeasurement,
  questionLogic,
  questionResponse,
  user,
  overallResult,
  attributeResult,
  attributeValueResult,
  surveyElementResult,
  categoryResult,
  compareResult,
  surveyReminder,
  benchmarkAttribute,
  companyBenchmarkSetting,
  benchmarkQuestion,
  surveyTemplate,
  lifecycleSurveyType,
  lifecycleSurveyTrigger,
  lifecycleSurveyDoNotContactList,
  rehireAttribute,
};
